import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, ClipboardList, NotebookPen, Send, MessageCircle, MessageSquare, X, Zap } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api, trackClicSoumission } from "../lib/api";
import { FadeUp, MaskedLine } from "../components/motion";
import RichText from "../components/RichText";
import { ShareButtons } from "../components/ShareButtons";
import { ShareMenu } from "../components/ShareMenu";
import { catNoOf } from "../lib/localize";
import { SEO } from "../components/SEO";
import { CallRequestDialog } from "../components/CallRequestDialog";

const STEP_ICONS = [NotebookPen, ClipboardList, Send];

// PAGE CONTENEUR plein écran (demande du propriétaire) : le catalogue fournisseur
// s'affiche DANS mrtrophee.ca — on ne quitte plus le site. Échap ou ✕ pour fermer,
// repli « ouvrir chez le fournisseur » toujours visible (certains sites peuvent
// refuser l'intégration), http→https contre le blocage « contenu mixte ».
function CatalogueViewerOverlay({ title, url, catNo, onClose, testid }) {
  const { t } = useLang();
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  const httpsUrl = (url || "").replace(/^http:\/\//i, "https://");
  return (
    <div className="fixed inset-0 z-[9995] flex flex-col bg-ink" data-testid={testid}>
      {/* z 9995 : AU-DESSUS des boutons flottants (Soumission 30 s z-9990, clavardage)
          pour qu'ils ne chevauchent pas le catalogue affiché (demande du propriétaire) */}
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3 border-b border-gold/20 bg-ink shrink-0">
        <p className="font-serif text-base sm:text-lg text-ivory truncate flex-1" data-testid={`${testid}-title`}>
          {title}
        </p>
        {["1.0", "2.0", "3.0"].includes(catNo) && (
          <Link to={`/boutique?cn=${catNo}`} data-testid={`${testid}-shop`}
            className="hidden sm:inline-flex items-center gap-2 border border-gold/40 text-gold font-semibold px-4 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300">
            {t("catalogues.viewer_shop")}
          </Link>
        )}
        {/* Soumission 30 s depuis le catalogue ouvert (demande du propriétaire,
            2026-10-17) : le NOM du catalogue suit et pré-remplit la soumission */}
        <Link to={`/soumission-rapide?catalogue=${encodeURIComponent(title)}`}
          onClick={() => trackClicSoumission("catalogues")}
          data-testid={`${testid}-quote`}
          className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-4 py-2 rounded-full text-xs hover:bg-champagne transition-colors duration-300">
          <Zap className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t("quote.fab")}</span>
        </Link>
        <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-supplier`}
          title="Si le catalogue ne s'affiche pas ci-dessous, ouvrez-le chez le fournisseur"
          className="inline-flex items-center gap-2 text-ivory/70 hover:text-gold font-semibold px-3 sm:px-4 py-2 rounded-full text-xs border border-ivory/15 hover:border-gold/40 transition-colors duration-300">
          <ExternalLink className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t("catalogues.popup_open")}</span>
        </a>
        <button onClick={onClose} data-testid={`${testid}-close`}
          className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-ivory/15 text-ivory/70 hover:text-gold hover:border-gold/40 transition-colors duration-300"
          aria-label="Fermer">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="relative flex-1 bg-white">
        {!loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-neutral-500">
            <span className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-700 rounded-full animate-spin" />
            <span className="text-xs">Chargement du catalogue…</span>
          </div>
        )}
        <iframe src={httpsUrl} title={title} onLoad={() => setLoaded(true)}
          data-testid={`${testid}-frame`}
          className="absolute inset-0 w-full h-full border-0" allow="fullscreen" />
      </div>
    </div>
  );
}

// Lien catalogue : miniature = couverture du catalogue (1re page, servie depuis notre
// stockage) + menu déroulant de partage. Catalogue importé → visionneuse interactive
// interne ; catalogue EXTERNE (site fournisseur) → PAGE CONTENEUR plein écran dans
// mrtrophee.ca (demande du propriétaire — on ne quitte plus le site).
function CatalogueLink({ l, i, k, covers, interactive }) {
  const { t, ts } = useLang();
  const [open, setOpen] = useState(false);
  const catNo = catNoOf(l.label);
  const src = (catNo && covers[catNo]) || "";
  // Catalogue importé chez nous → visionneuse interactive interne. Un lien marqué
  // « external » (ex. nº 1.0 → awardsofdistinction.ca, demande du propriétaire)
  // ouvre TOUJOURS le conteneur plein écran, même s'il existe des pages internes.
  const internal = !l.external && interactive.includes(catNo);
  const [main, sub] = l.label.split(" — ");
  const shareUrl = internal
    ? `${window.location.origin}/catalogue-interactif?cat=${catNo}`
    : l.url;

  const footer = (
    <span className="flex items-center gap-2 p-3.5">
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-ivory/90 group-hover:text-gold transition-colors duration-300 truncate">{main}</span>
        {sub && <span className="block text-xs text-ivory/70 group-hover:text-gold/70 transition-colors duration-300 truncate">{sub}</span>}
      </span>
      <ShareMenu url={shareUrl} title={l.label} testid={`catalog-share-${i}-${k}`} />
      <ExternalLink className="w-4 h-4 shrink-0 text-ivory/65 group-hover:text-gold transition-colors duration-300" />
    </span>
  );

  const body = src ? (
    <>
      <span className="block aspect-[3/4] bg-[#f4f2ec] overflow-hidden">
        <img src={src} alt={main} loading="lazy" data-testid={`catalog-cover-${i}-${k}`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
      </span>
      {footer}
    </>
  ) : (
    <span className="flex flex-col justify-center min-h-[4.5rem]">{footer}</span>
  );

  const cls = "group block rounded-lg border border-ivory/15 bg-ink/40 hover:border-gold hover:bg-gold/5 transition-colors duration-300 overflow-hidden";

  // Catalogue importé chez nous → visionneuse interactive interne
  if (internal) {
    return <Link to={`/catalogue-interactif?cat=${catNo}`} data-testid={`catalog-link-${i}-${k}`} className={cls}>{body}</Link>;
  }

  // PDF fournisseur → visionneuse interne (le navigateur lit les PDF nativement)
  if (/\.pdf($|[?#])/i.test(l.url)) {
    const href = `/catalogue-viewer?src=${encodeURIComponent(l.url)}&nom=${encodeURIComponent(l.label)}`;
    return <Link to={href} data-testid={`catalog-link-${i}-${k}`} className={cls}>{body}</Link>;
  }

  // Catalogue externe → PAGE CONTENEUR plein écran (demande du propriétaire) : le
  // catalogue fournisseur s'affiche DANS mrtrophee.ca — on ne quitte plus le site.
  return (
    <>
      <div role="button" tabIndex={0} onClick={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen(true); }}
        data-testid={`catalog-link-${i}-${k}`} className={`${cls} cursor-pointer`}>
        {body}
      </div>
      {open && (
        <CatalogueViewerOverlay title={l.label} url={l.url} catNo={catNo}
          onClose={() => setOpen(false)} testid={`catalog-viewer-${i}-${k}`} />
      )}
    </>
  );
}

// Carte d'un catalogue ajouté par l'admin. PDF téléversé → visionneuse interne ;
// LIEN EXTERNE (e-catalogue fournisseur) → popup miniature + lien (les visionneuses
// fournisseurs refusent l'intégration en iframe).
function CustomCatalogueCard({ c }) {
  const { t, ts } = useLang();
  const [open, setOpen] = useState(false);
  const isExternal = !!c.url;
  const target = c.url || c.file_url;
  const href = `/catalogue-viewer?src=${encodeURIComponent(target)}&nom=${encodeURIComponent(c.title)}`;
  const shareUrl = isExternal ? c.url : `${window.location.origin}${href}`;

  const inner = (
    <>
      {c.cover_url && (
        <span className="block aspect-[3/4] bg-[#f4f2ec] overflow-hidden">
          <img
            src={c.cover_url} alt="" loading="lazy"
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </span>
      )}
      <span className="flex items-center justify-between gap-2 px-3 py-2.5">
        <span className="min-w-0">
          <span className="block text-xs text-ivory/80 truncate group-hover:text-gold transition-colors duration-300">
            {c.title}
          </span>
          <span className="block text-[10px] text-ivory/45">
            {c.url ? "E-catalogue" : `PDF · ${c.pages} p.`}
          </span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <ShareMenu url={shareUrl} title={c.title} label="" />
          <ExternalLink className="w-3.5 h-3.5 text-gold/70 group-hover:text-gold transition-colors duration-300" />
        </span>
      </span>
    </>
  );

  const cls = "group block rounded-lg border border-ivory/10 bg-ink/40 overflow-hidden hover:border-gold/50 transition-colors duration-300";

  if (isExternal && !/\.pdf($|[?#])/i.test(c.url)) {
    // E-catalogue externe (admin) → même page conteneur plein écran (demande du propriétaire)
    return (
      <>
        <div role="button" tabIndex={0} onClick={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen(true); }}
          data-testid={`custom-catalog-${c.id}`} className={`${cls} cursor-pointer`}>
          {inner}
        </div>
        {open && (
          <CatalogueViewerOverlay title={c.title} url={c.url} catNo=""
            onClose={() => setOpen(false)} testid={`custom-catalog-viewer-${c.id}`} />
        )}
      </>
    );
  }
  return <Link to={href} data-testid={`custom-catalog-${c.id}`} className={cls}>{inner}</Link>;
}

export default function Catalogues() {
  const { t, ts, lang } = useLang();
  const steps = t("catalogues.steps").map((s, i) => ({ text: s, icon: STEP_ICONS[i], num: `0${i + 1}` }));
  const groups = t("catalogues.groups");
  const [covers, setCovers] = useState({});
  const [interactive, setInteractive] = useState([]);
  const [custom, setCustom] = useState([]);
  const [disabledLinks, setDisabledLinks] = useState([]);  // liens masqués par l'admin (par URL)

  useEffect(() => {
    api.get("/catalogue-covers")
      .then((r) => {
        setCovers(r.data?.covers || {});
        setInteractive(r.data?.interactive || []);
        setCustom(r.data?.custom || []);
        setDisabledLinks(r.data?.disabled_links || []);
      })
      .catch(() => {});
  }, []);

  // Catalogues ajoutés par l'admin : rattachés à une section existante (même nom,
  // insensible à la casse) ou regroupés dans de NOUVELLES sections à la suite.
  const customBySection = {};
  custom.forEach((c) => {
    const key = (c.section || "").trim().toLowerCase();
    if (key) (customBySection[key] = customBySection[key] || []).push(c);
  });
  const staticTitles = new Set(groups.map((g) => g.title.trim().toLowerCase()));
  const extraSections = [];
  custom.forEach((c) => {
    const s = (c.section || "").trim();
    if (s && !staticTitles.has(s.toLowerCase()) && !extraSections.includes(s)) extraSections.push(s);
  });

  return (
    <div data-testid="catalogues-page" className="pt-32 pb-28">
      <SEO title={ts("seo.catalogues_title")} description={ts("seo.catalogues_desc")} path="/catalogues"
        keywords={lang === "fr"
          ? "catalogues trophées, catalogue médailles personnalisées, catalogue plaques honorifiques, fournisseurs récompenses Québec, catalogue cadeaux d'entreprise, catalogue gravure, catalogues trophées Montérégie"
          : "trophy catalogues, custom medals catalogue, honour plaques catalogue, awards suppliers Quebec, corporate gifts catalogue, engraving catalogue, trophy catalogues Monteregie"}
        jsonLd={[
          { "@context": "https://schema.org", "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: "https://mrtrophee.ca/" },
              { "@type": "ListItem", position: 2, name: lang === "fr" ? "Les Catalogues" : "Catalogues", item: "https://mrtrophee.ca/catalogues" },
            ] },
          { "@context": "https://schema.org", "@type": "CollectionPage",
            name: ts("seo.catalogues_title"), description: ts("seo.catalogues_desc"),
            url: "https://mrtrophee.ca/catalogues", inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
            mainEntity: { "@type": "ItemList",
              itemListElement: [
                ...groups.flatMap((g) => g.links || []),
                ...custom,
              ].slice(0, 40).map((l, i) => ({
                "@type": "ListItem", position: i + 1, name: l.name || l.title,
              })) } },
        ]} />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("catalogues.kicker")}</p>
          <h1 className="font-serif text-5xl md:text-7xl leading-tight mb-6">
            <MaskedLine key={ts("catalogues.title_1")} delay={0.15}>{t("catalogues.title_1")}</MaskedLine>
            <MaskedLine key={ts("catalogues.title_2")} delay={0.35} className="italic text-gold">{t("catalogues.title_2")}</MaskedLine>
          </h1>
          <p className="text-ivory/60 max-w-xl mb-14">{t("catalogues.intro")}</p>
          <ShareButtons title={`${ts("catalogues.title_1")} ${ts("catalogues.title_2")} — Mr Trophée`} label={t("footer.share_page")} />
          <div className="mb-14" />
        </FadeUp>

        {/* Création sur mesure — parcours guidé, EN HAUT de la page (demande du
            propriétaire, 2026-10-17) : toute la section (titre, texte, gros
            bouton Soumission en 30 secondes + 4 canaux) est remontée ici à la
            place du bouton « soumission rapide » isolé qui suivait les étapes */}
        <FadeUp className="mb-16">
          <div className="border border-gold/25 rounded-2xl p-10 md:p-14 text-center spotlight" data-testid="custom-journey">
            <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("catalogues.custom_kicker")}</p>
            <h2 className="font-serif text-3xl md:text-5xl mb-4">{t("catalogues.custom_title")}</h2>
            <p className="text-ivory/60 max-w-xl mx-auto mb-8">{t("catalogues.custom_sub")}</p>
            <div className="mb-10">
              <Link
                to="/soumission-rapide" data-testid="custom-quick-quote"
                onClick={() => trackClicSoumission("catalogues")}
                className="group inline-flex items-center gap-3 bg-gold text-ink font-bold text-lg md:text-xl px-10 md:px-14 py-5 rounded-full shadow-[0_0_40px_rgba(212,175,55,0.35)] hover:bg-champagne hover:shadow-[0_0_55px_rgba(212,175,55,0.5)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
              >
                <Zap className="w-6 h-6 transition-transform duration-300 group-hover:-rotate-12" />
                {t("quote.fab_long")}
                <ArrowRight className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://m.me/trophee.ca" target="_blank" rel="noreferrer" data-testid="custom-messenger"
                className="group inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300"
              >
                <MessageCircle className="w-5 h-5" /> Messenger
              </a>
              <a
                href="sms:+15145656665" data-testid="custom-texto"
                className="inline-flex items-center gap-3 border border-gold/40 text-gold px-8 py-4 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <MessageSquare className="w-5 h-5" /> {t("footer.texto")}
              </a>
              <CallRequestDialog testid="custom-call"
                className="inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300" />
              <Link
                to="/contact" data-testid="custom-form"
                className="inline-flex items-center gap-3 border border-ivory/25 text-ivory px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
              >
                <ClipboardList className="w-5 h-5" /> {t("catalogues.custom_form")}
              </Link>
            </div>
          </div>
        </FadeUp>

        {/* 3 étapes */}
        <div className="grid sm:grid-cols-3 gap-6 mb-12">
          {steps.map((s, i) => (
            <FadeUp key={s.num} delay={parseInt(s.num) * 0.08}>
              <div className="bg-surface border border-gold/15 rounded-xl p-7 h-full" data-testid={`catalogues-step-${s.num}`}>
                <span className="font-serif text-4xl text-outline block mb-4">{s.num}</span>
                <p className="text-ivory/70 text-sm leading-relaxed"><RichText text={s.text} path={`catalogues.steps.${i}`} /></p>
              </div>
            </FadeUp>
          ))}
        </div>

        {/* Groupes de catalogues */}
        <div className="space-y-8">
          {groups.map((g, i) => {
            // Liens masqués par l'admin (Paramètres → Catalogues). Section entièrement
            // sautée si tous ses liens sont masqués et qu'aucun catalogue personnalisé
            // n'y est rattaché.
            const visibleLinks = g.links.filter((l) => !disabledLinks.includes(l.url));
            const sectionCustoms = customBySection[g.title.trim().toLowerCase()] || [];
            if (!visibleLinks.length && !sectionCustoms.length) return null;
            return (
            <FadeUp key={i} delay={i * 0.05}>
              <div className="bg-surface border border-ivory/10 rounded-xl p-7 hover:border-gold/30 transition-colors duration-500" data-testid={`catalog-group-${i}`}>
                <h2 className="font-serif text-2xl text-champagne mb-5"><RichText text={g.title} path={`catalogues.groups.${i}.title`} /></h2>
                {/* 3 miniatures par ligne en mobile, 5 sur PC */}
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2.5">
                  {visibleLinks.map((l, k) => (
                    <CatalogueLink key={k} l={l} i={i} k={k} covers={covers} interactive={interactive} />
                  ))}
                  {sectionCustoms.map((c) => (
                    <CustomCatalogueCard key={c.id} c={c} />
                  ))}
                </div>
              </div>
            </FadeUp>
            );
          })}
          {extraSections.map((s) => (
            <FadeUp key={`custom-${s}`} delay={0.05}>
              <div className="bg-surface border border-ivory/10 rounded-xl p-7 hover:border-gold/30 transition-colors duration-500"
                data-testid={`catalog-group-custom-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                <h2 className="font-serif text-2xl text-champagne mb-5">{s}</h2>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2.5">
                  {(customBySection[s.toLowerCase()] || []).map((c) => (
                    <CustomCatalogueCard key={c.id} c={c} />
                  ))}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

      </div>
    </div>
  );
}
